import React from "react";

const benefits = [
  {
    num: "1",
    numStyle: "text-white/70 border-primary-soft bg-primary-soft/30",
    title: "Eliminate Vendor Lock-In",
    desc: "Vector database migration strategies ensure you're not dependent on a single provider's ecosystem, pricing model, or roadmap. By maintaining the ability to migrate between platforms like Pinecone, Qdrant, Weaviate, Milvus, and Chroma, organisations retain negotiating power and technical flexibility.",
    advantage: "Preserve optionality to adopt new vector database technologies as they emerge, without costly re-engineering efforts.",
  },
  {
    num: "2",
    numStyle: "text-white border-primary-soft bg-primary/50",
    title: "Optimise Performance & Cost",
    desc: "Different vector databases excel at different workloads. Migration enables teams to match database capabilities with specific requirements—whether that's optimising for query latency, indexing speed, memory efficiency, or total cost of ownership at scale.",
    advantage: "Achieve 2-10x performance improvements and 30-70% cost reductions by selecting the optimal vector database for your workload characteristics.",
  },
  {
    num: "3",
    numStyle: "text-white/60 border-primary-soft bg-primary-soft/20",
    title: "Support Evolving AI Architectures",
    desc: "As LLM and embedding models evolve, your vector database infrastructure must adapt. Migration capabilities ensure seamless transitions when upgrading to new embedding dimensions, adopting multi-vector approaches, or implementing hybrid dense-sparse retrieval systems.",
    advantage: "Stay current with latest RAG innovations without disrupting production systems or regenerating entire embedding collections.",
  },
  {
    num: "4",
    numStyle: "text-white border-primary-soft bg-primary-dark/50",
    title: "Enable Safe Experimentation",
    desc: "Vector database migration workflows allow teams to test new platforms in staging environments with production data, validate performance improvements, and roll back if necessary—all without risk to live systems.",
    advantage: "Make data-driven infrastructure decisions based on real workload testing rather than theoretical benchmarks.",
  },
];

const bestPractices = [
  "Implement dual-write strategies during transition periods",
  "Validate embedding integrity with checksums and sampling",
  "Use incremental migration with automatic fallback mechanisms",
  "Benchmark query performance before cutover",
];

export default function MigrationWhySection() {
  return (
    <section id="why" className="mb-20 scroll-mt-20">

      <div className="flex items-center gap-3 mb-6">
        <div className="w-1 h-12 bg-gradient-to-b from-white/60 to-white/20 rounded-full" />
        <h2 className="text-4xl font-bold text-white">Why Migrate Vector Databases?</h2>
      </div>

      <div className="bg-primary-soft/30 border border-primary-soft rounded-2xl p-8 mb-8">
        <p className="text-white/70 text-lg leading-relaxed mb-6">
          Vector database migration empowers AI engineering teams to maintain technological
          agility in a rapidly evolving ecosystem. As the landscape of vector databases
          continues to mature, organisations must balance performance requirements, cost
          constraints, and operational complexity while ensuring their AI infrastructure
          remains future-proof.
        </p>
        <p className="text-white/70 text-lg leading-relaxed">
          Strategic migration planning prevents vendor lock-in, enables experimentation with
          emerging technologies, and ensures your vector embeddings remain portable across
          platforms. This flexibility is critical for organisations building mission-critical
          AI applications that must adapt to changing business requirements and technological
          advances.
        </p>
      </div>

      <h3 className="text-2xl font-semibold mb-6 text-white">
        Strategic Benefits of Vector Database Migration
      </h3>

      <div className="space-y-6 mb-8">
        {benefits.map((item, idx) => (
          <div
            key={idx}
            className="bg-primary-soft/20 border border-primary-soft rounded-xl p-6 hover:border-white/60 transition-colors"
          >
            <h4 className="font-semibold text-xl mb-3 text-white flex items-center gap-3">
              <span className={`w-8 h-8 rounded-lg border flex items-center justify-center font-bold ${item.numStyle}`}>
                {item.num}
              </span>
              {item.title}
            </h4>
            <p className="text-white/70 leading-relaxed mb-4">{item.desc}</p>
            <div className="pl-11 text-white/50 text-sm">
              <strong className="text-white/80">Key advantage:</strong> {item.advantage}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-primary-soft/10 border border-primary-soft hover:border-white/60 rounded-xl p-6 transition-colors">
        <h4 className="font-semibold text-lg mb-4 text-white">
          Migration Best Practices for Zero Downtime
        </h4>
        <div className="grid md:grid-cols-2 gap-4 text-white/70">
          {bestPractices.map((tip, i) => (
            <div key={i} className="flex items-start gap-3">
              <svg className="w-5 h-5 text-white mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{tip}</span>
            </div>
          ))}
        </div>
      </div>

    </section>
  );
}